import React from 'react';
import { Stack, Typography } from '@mui/material';
import { useListAllBrokerGroupsQuery } from '@/admin/brokerGroups/api/brokerGroupsApi';
import { useListAllFeatureFlagsQuery } from '@/admin/featureFlags/api/featureFlagsApi';
import { USER_FIELD_LABELS, USER_PERMISSION_FIELDS } from './userFormValues';

const NONE = '(none)';

/**
 * The save confirmation's list of changes, one "Field: old → new" line each (from
 * getUserChanges), then the feature flags being turned on and off for the user
 * (featureFlagChanges, from getFeatureFlagChanges). Broker groups and flags show by name,
 * permissions as Yes / No, a photo as "New photo", and empty values as "(none)". Without
 * `before` (a new user), each line is just "Field: value".
 */
export const UserChangeList = ({ changed, before, after, featureFlagChanges }) => {
	const { data: brokerGroups = [] } = useListAllBrokerGroupsQuery();
	const { data: flags = [] } = useListAllFeatureFlagsQuery();

	const brokerGroupName = (id) => brokerGroups.find((item) => item.id === id)?.name ?? id;
	const flagNames = (ids) =>
		ids.map((id) => flags.find((flag) => flag.id === id)?.feature ?? id).join(', ');

	const displayValue = (field, value) => {
		if (USER_PERMISSION_FIELDS.includes(field)) return value ? 'Yes' : 'No';
		if (field === 'brokerGroupArray') return value?.length ? value.map(brokerGroupName).join(', ') : NONE;
		if (value === null || value === undefined || value === '') return NONE;
		if (field === 'brokerGroupId') return brokerGroupName(value);
		return String(value);
	};

	const flagLines = [
		['Turn on', featureFlagChanges.added],
		['Turn off', featureFlagChanges.removed],
	].filter(([, ids]) => ids.length);

	return (
		<Stack spacing={0.5}>
			{changed.map((field) => (
				<Typography key={field}>
					<span style={{ fontWeight: 'bold' }}>{`${USER_FIELD_LABELS[field]}: `}</span>
					<span>
						{(field === 'imageUrl' && 'New photo') ||
							(before
								? `${displayValue(field, before[field])} → ${displayValue(field, after[field])}`
								: displayValue(field, after[field]))}
					</span>
				</Typography>
			))}
			{flagLines.map(([label, ids]) => (
				<Typography key={label}>
					<span style={{ fontWeight: 'bold' }}>{`${label} feature flags: `}</span>
					<span>{flagNames(ids)}</span>
				</Typography>
			))}
		</Stack>
	);
};
